import { BookOpen, CircleHelp, MessageCircle, Settings } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { iconButtonSurfaceVariants, iconButtonVariants } from '@/components/ui/icon-button-variants';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '../ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { TOOLTIP_APPEARANCE } from '../ui/types/tooltip-variants-enums';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  getActionsForSurface,
} from '@/lib/navigation/account-action-catalog';
import {
  AccountActionId,
  AccountActionKind,
  AccountMenuSurface,
} from '@/lib/navigation/types/account-action-catalog-enums';
import { useResolvedAccountActionCatalog } from '@/hooks/navigation/use-resolved-account-action-catalog';
import { cn } from '@/lib/utils';
import { useIntercom } from '@/contexts/intercom/intercom-context';

import { PAGE_TOPBAR_UTILITY_VISIBILITY } from './types/page-topbar-utility-actions-variants-enums';

const SETTINGS_PATH_PREFIX = '/account/settings';



interface PageTopbarUtilityActionsProps {
  className?: string;
  hideSettings?: boolean;
  visibility?: PAGE_TOPBAR_UTILITY_VISIBILITY;
}

function UtilityTooltip({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent appearance={TOOLTIP_APPEARANCE.FOUNDATION}>{label}</TooltipContent>
    </Tooltip>
  );
}

function UtilityIconLink({
  href,
  label,
  tooltip,
  external,
  children,
}: {
  href: string;
  label: string;
  tooltip: string;
  external?: boolean;
  children: ReactNode;
}) {
  const className = cn(
    iconButtonVariants({
      variant: ICON_BUTTON_VARIANT.GHOST,
      size: ICON_BUTTON_SIZE.COMPACT,
    })
  );
  const surfaceClassName = cn(
    iconButtonSurfaceVariants({
      variant: ICON_BUTTON_VARIANT.GHOST,
      size: ICON_BUTTON_SIZE.COMPACT,
    })
  );
  const content = (
    <span aria-hidden="true" className={surfaceClassName} data-slot="icon-button-surface">
      {children}
    </span>
  );

  return (
    <UtilityTooltip label={tooltip}>
      {external ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          className={className}
        >
          {content}
        </a>
      ) : (
        <Link to={href} aria-label={label} className={className}>
          {content}
        </Link>
      )}
    </UtilityTooltip>
  );
}

export function PageTopbarUtilityActions({
  className,
  hideSettings = false,
  visibility = PAGE_TOPBAR_UTILITY_VISIBILITY.DESKTOP_ONLY,
}: PageTopbarUtilityActionsProps) {
  const location = useLocation();
  const catalog = useResolvedAccountActionCatalog();
  const topbarActions = getActionsForSurface(catalog, AccountMenuSurface.DESKTOP_TOPBAR);
  const { show: showIntercom } = useIntercom();

  const isOnSettings = location.pathname.startsWith(SETTINGS_PATH_PREFIX);
  const suppressSettings = hideSettings || isOnSettings;

  const helpAction = topbarActions.find(a => a.id === AccountActionId.OPEN_HELP);
  const liveChatAction = topbarActions.find(a => a.id === AccountActionId.LIVE_CHAT);
  const docsAction = topbarActions.find(a => a.id === AccountActionId.DOCUMENTATION);
  const settingsAction = topbarActions.find(a => a.id === AccountActionId.WORKSPACE_SETTINGS);

  return (
    <div
      className={cn(
        'items-center gap-1',
        visibility === PAGE_TOPBAR_UTILITY_VISIBILITY.ALWAYS
          ? 'flex'
          : 'hidden md:flex',
        className
      )}
      data-slot="page-topbar-utility-actions"
    >
      {helpAction ? (
        <UtilityTooltip label={helpAction.label}>
          <IconButton
            label={helpAction.label}
            size={ICON_BUTTON_SIZE.COMPACT}
            variant={ICON_BUTTON_VARIANT.GHOST}
            onClick={showIntercom}
          >
            <CircleHelp aria-hidden="true" />
          </IconButton>
        </UtilityTooltip>
      ) : null}

      {liveChatAction ? (
        <UtilityTooltip label={liveChatAction.label}>
          <IconButton
            label={liveChatAction.label}
            size={ICON_BUTTON_SIZE.COMPACT}
            variant={ICON_BUTTON_VARIANT.GHOST}
            onClick={showIntercom}
          >
            <MessageCircle aria-hidden="true" />
          </IconButton>
        </UtilityTooltip>
      ) : null}

      {docsAction?.kind === AccountActionKind.EXTERNAL_LINK && docsAction.href ? (
        <UtilityIconLink
          href={docsAction.href}
          label={`${docsAction.label} (opens in new tab)`}
          tooltip={`${docsAction.label} (opens in new tab)`}
          external
        >
          <BookOpen aria-hidden="true" />
        </UtilityIconLink>
      ) : null}

      {!suppressSettings &&
      settingsAction?.kind === AccountActionKind.LINK &&
      settingsAction.href ? (
        <UtilityIconLink
          href={settingsAction.href}
          label={settingsAction.label}
          tooltip={settingsAction.label}
        >
          <Settings aria-hidden="true" />
        </UtilityIconLink>
      ) : null}
    </div>
  );
}
